import { createTextEditorHandler, TextEditorHandler } from '@lessonup/pins-shared';
import { isRightMBClick } from '@lessonup/ui-components';
import { Editor } from '@tiptap/core';
import { useCallback, useEffect, useRef } from 'react';
import { CategoryTransformAction } from '../../../types/pinComponents/pinTransformActions';

export interface CategoryHandlerProps {
  componentId: string;
  categoryId: string;
  active: boolean;
  editable: boolean;
  isTextEditMode: boolean;
  setModeTextEdit?: (componentId: string, categoryId: string) => void;
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement>, action: CategoryTransformAction) => void;
  onMouseUp?: (event: React.MouseEvent<HTMLDivElement>) => void;
  layoutMode?: 'AUTO' | 'CUSTOM';
}

export function useCategoryHandlers(props: CategoryHandlerProps) {
  const {
    componentId,
    categoryId,
    active,
    editable,
    isTextEditMode,
    setModeTextEdit,
    onMouseDown,
    onMouseUp,
    layoutMode,
  } = props;

  const categoryRef = useRef<HTMLDivElement>(null);
  const textEditorHandlerRef = useRef<TextEditorHandler | null>(null);

  const clickState = useRef({
    shouldTryForDetailMode: false,
    dragged: false,
  });

  const registerEditor = useCallback(
    (editor: Editor) => {
      textEditorHandlerRef.current = createTextEditorHandler(editor);

      if (isTextEditMode && active) {
        setTimeout(() => {
          textEditorHandlerRef.current?.focus.end();
        }, 1);
      }
    },
    [isTextEditMode, active]
  );

  useEffect(() => {
    if (!active) {
      clickState.current.dragged = false;
      clickState.current.shouldTryForDetailMode = false;
    }
  }, [active]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!editable || !active) return;

      if (isRightMBClick(event)) {
        if (isTextEditMode) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }

      if (active && !isTextEditMode) {
        clickState.current.shouldTryForDetailMode = true;
        event.stopPropagation();
      } else {
        textEditorHandlerRef.current?.blur();
      }
    },
    [active, isTextEditMode, editable]
  );

  const handleMouseUp = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!active || !editable) return;

      if ((!isTextEditMode && !clickState.current.shouldTryForDetailMode) || clickState.current.dragged) {
        clickState.current.dragged = false;
        clickState.current.shouldTryForDetailMode = false;
        return;
      }

      clickState.current.shouldTryForDetailMode = false;

      if (!isTextEditMode) {
        if (layoutMode === 'CUSTOM') {
          onMouseUp?.(event);
        }

        setModeTextEdit?.(componentId, categoryId);
        event.stopPropagation();
      }
    },
    [active, isTextEditMode, editable, setModeTextEdit, componentId, categoryId, layoutMode, onMouseUp]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!active || !clickState.current.shouldTryForDetailMode) return;

      if (!clickState.current.dragged && layoutMode === 'CUSTOM') {
        clickState.current.dragged = true;
        onMouseDown?.(event, { action: 'move-category', componentId, categoryId });
      }

      textEditorHandlerRef.current?.blur();
    },
    [active, layoutMode, onMouseDown, componentId, categoryId]
  );

  return {
    categoryRef,
    registerEditor,
    handleMouseDown,
    handleMouseUp,
    handleMouseMove,
  };
}
